import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  createChart,
  IChartApi,
  ISeriesApi,
  CandlestickData,
  LineData,
  Time,
  CrosshairMode,
  CandlestickSeries,
  LineSeries,
  HistogramSeries
} from 'lightweight-charts';
import { apiService } from '../../services/api';
import './FullscreenChartModal.css';

interface FullscreenChartModalProps {
  isOpen: boolean;
  onClose: () => void;
  symbol: string;
  companyName?: string;
  currentPrice?: number;
}

interface ChartBar {
  timestamp: number;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number | null;
  volume: number | null;
  sma_50: number | null;
  sma_200: number | null;
  rsi_14: number | null;
  macd_line?: number | null;
  signal_line?: number | null;
  macd_histogram?: number | null;
}

interface IVDataPoint {
  timestamp: number;
  avg_iv: number | null;
}

interface HoverData {
  time: Time;
  open: number;
  high: number;
  low: number;
  close: number;
}

type Period = '1M' | '3M' | '6M';

const FullscreenChartModal: React.FC<FullscreenChartModalProps> = ({
  isOpen,
  onClose,
  symbol,
  companyName,
  currentPrice
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const ivContainerRef = useRef<HTMLDivElement>(null);
  const rsiContainerRef = useRef<HTMLDivElement>(null);
  const macdContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const ivChartRef = useRef<IChartApi | null>(null);
  const rsiChartRef = useRef<IChartApi | null>(null);
  const macdChartRef = useRef<IChartApi | null>(null);
  const candlestickSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const sma50SeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const sma200SeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const ivSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const rsiSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const macdLineSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const macdSignalSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const macdHistSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);

  const [selectedPeriod, setSelectedPeriod] = useState<Period>('3M');
  const [showSMA50, setShowSMA50] = useState(true);
  const [showSMA200, setShowSMA200] = useState(true);
  // Collapse indicator panes by default on mobile for more chart space
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const [showIV, setShowIV] = useState(!isMobile);
  const [showRSI, setShowRSI] = useState(!isMobile);
  const [showMACD, setShowMACD] = useState(!isMobile);

  // Pane height management for drag resizing
  const DIVIDER_HEIGHT = 6;
  const MIN_PANE_HEIGHT = 40;
  const DEFAULT_INDICATOR_HEIGHT = isMobile ? 60 : 80;
  const [paneHeights, setPaneHeights] = useState<{ iv: number; rsi: number; macd: number }>({
    iv: DEFAULT_INDICATOR_HEIGHT,
    rsi: DEFAULT_INDICATOR_HEIGHT,
    macd: DEFAULT_INDICATOR_HEIGHT
  });
  const dragRef = useRef<{
    pane: 'iv' | 'rsi' | 'macd';
    startY: number;
    startHeight: number;
  } | null>(null);

  // Drag handlers for resizable dividers
  const handleDragStart = useCallback((pane: 'iv' | 'rsi' | 'macd', clientY: number) => {
    dragRef.current = {
      pane,
      startY: clientY,
      startHeight: paneHeights[pane]
    };
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
  }, [paneHeights]);

  const handleDragMove = useCallback((clientY: number) => {
    if (!dragRef.current || !wrapperRef.current) return;
    const { pane, startY, startHeight } = dragRef.current;
    const delta = startY - clientY; // dragging up = pane grows
    const newHeight = Math.max(MIN_PANE_HEIGHT, startHeight + delta);
    // Cap at 50% of wrapper height
    const maxHeight = wrapperRef.current.clientHeight * 0.5;
    setPaneHeights(prev => ({ ...prev, [pane]: Math.min(newHeight, maxHeight) }));
  }, []);

  const handleDragEnd = useCallback(() => {
    dragRef.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Global mouse/touch move+end listeners for drag
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => handleDragMove(e.clientY);
    const onTouchMove = (e: TouchEvent) => handleDragMove(e.touches[0].clientY);
    const onEnd = () => handleDragEnd();

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onTouchMove);
    window.addEventListener('touchend', onEnd);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [handleDragMove, handleDragEnd]);

  const [hoverData, setHoverData] = useState<HoverData | null>(null);
  const [displayData, setDisplayData] = useState<HoverData | null>(null);
  const [chartData, setChartData] = useState<ChartBar[]>([]);
  const [ivData, setIVData] = useState<IVDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter data by period (client-side)
  const filterDataByPeriod = useCallback((data: ChartBar[], period: Period): ChartBar[] => {
    const periodDays: Record<Period, number> = { '1M': 30, '3M': 90, '6M': 180 };
    const cutoff = Date.now() - (periodDays[period] * 24 * 60 * 60 * 1000);
    const filtered = data.filter(bar => bar.timestamp >= cutoff);
    console.log(`📅 Price filter ${period}: cutoff=${new Date(cutoff).toLocaleDateString()}, ${filtered.length}/${data.length} bars`);
    return filtered;
  }, []);

  // Filter IV data by period (client-side)
  const filterIVDataByPeriod = useCallback((data: IVDataPoint[], period: Period): IVDataPoint[] => {
    const periodDays: Record<Period, number> = { '1M': 30, '3M': 90, '6M': 180 };
    const cutoff = Date.now() - (periodDays[period] * 24 * 60 * 60 * 1000);
    return data.filter(point => point.timestamp >= cutoff);
  }, []);

  // Helper to convert timestamp to seconds
  const toSeconds = useCallback((timestamp: number): number => {
    return timestamp > 10000000000 ? Math.floor(timestamp / 1000) : timestamp;
  }, []);

  // Fetch chart data when modal opens
  useEffect(() => {
    if (!isOpen || !symbol) return;

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Calculate 6-month date range
        const endDate = new Date();
        const startDate = new Date();
        startDate.setMonth(endDate.getMonth() - 6);

        const fromDate = startDate.toISOString().split('T')[0];
        const toDate = endDate.toISOString().split('T')[0];

        console.log(`📊 Fetching chart data with indicators for ${symbol}...`);
        const response = await apiService.getChartDataWithIndicators(symbol, fromDate, toDate, true);

        if (response.status === 'success' && response.data?.results) {
          // Sort by timestamp ascending
          const sortedData = [...response.data.results].sort((a, b) => a.timestamp - b.timestamp);
          console.log(`✅ Loaded ${sortedData.length} bars with indicators for ${symbol}`);
          setChartData(sortedData);

          // Set IV data if available
          if (response.data.iv_data) {
            const sortedIVData = [...response.data.iv_data].sort((a, b) => a.timestamp - b.timestamp);
            if (sortedIVData.length > 0) {
              const firstDate = new Date(sortedIVData[0].timestamp).toLocaleDateString();
              const lastDate = new Date(sortedIVData[sortedIVData.length - 1].timestamp).toLocaleDateString();
              console.log(`📈 Loaded ${sortedIVData.length} IV snapshots for ${symbol} (${firstDate} to ${lastDate})`);
            } else {
              console.log(`⚠️ No IV snapshots available for ${symbol}`);
            }
            setIVData(sortedIVData);
          }
        } else {
          setError('No data available');
        }
      } catch (err) {
        console.error('❌ Failed to fetch chart data:', err);
        setError('Failed to load chart data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [isOpen, symbol]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Initialize and update charts
  useEffect(() => {
    if (!isOpen || !chartContainerRef.current || chartData.length === 0) return;

    // Filter data based on selected period
    const filteredData = filterDataByPeriod(chartData, selectedPeriod);
    if (filteredData.length === 0) return;

    // Convert to lightweight-charts format (only bars with valid OHLC)
    const candlestickData: CandlestickData[] = filteredData
      .filter(bar => bar.open != null && bar.high != null && bar.low != null && bar.close != null)
      .map(bar => ({
        time: toSeconds(bar.timestamp) as Time,
        open: bar.open!,
        high: bar.high!,
        low: bar.low!,
        close: bar.close!
      }));

    if (candlestickData.length === 0) return;

    // Append today's realtime price if newer than last historical bar
    if (currentPrice && currentPrice > 0 && candlestickData.length > 0) {
      const lastBar = candlestickData[candlestickData.length - 1];
      const lastBarTimestamp = (lastBar.time as number) * 1000; // Convert back to ms
      const todayTimestamp = new Date().setHours(0, 0, 0, 0); // Start of today

      // Only append if today is newer than the last bar
      if (todayTimestamp > lastBarTimestamp) {
        const todayBar: CandlestickData = {
          time: toSeconds(todayTimestamp) as Time,
          open: lastBar.close, // Open at previous close
          high: Math.max(lastBar.close, currentPrice),
          low: Math.min(lastBar.close, currentPrice),
          close: currentPrice
        };
        candlestickData.push(todayBar);
        console.log(`📍 [Fullscreen] Appended today's price for ${symbol}: $${currentPrice.toFixed(2)}`);
      }
    }

    // Create main chart if not exists
    if (!chartRef.current) {
      chartRef.current = createChart(chartContainerRef.current, {
        localization: { locale: 'en-US' },
        layout: {
          background: { color: '#111215' },
          textColor: '#8b9097',
          attributionLogo: false
        },
        grid: {
          vertLines: { color: 'rgba(255, 255, 255, 0.045)' },
          horzLines: { color: 'rgba(255, 255, 255, 0.045)' }
        },
        crosshair: {
          mode: CrosshairMode.Normal,
          vertLine: {
            width: 1,
            color: '#6f747c',
            style: 1,
            labelBackgroundColor: '#1d1e23'
          },
          horzLine: {
            width: 1,
            color: '#6f747c',
            style: 1,
            labelBackgroundColor: '#1d1e23'
          }
        },
        rightPriceScale: {
          borderColor: 'rgba(255, 255, 255, 0.08)'
        },
        timeScale: {
          borderColor: 'rgba(255, 255, 255, 0.08)',
          timeVisible: true,
          secondsVisible: false
        },
        handleScroll: false,
        handleScale: false
      });

      // Add candlestick series
      candlestickSeriesRef.current = chartRef.current.addSeries(CandlestickSeries, {
        upColor: '#5cc79a',
        downColor: '#e08a7e',
        borderUpColor: '#5cc79a',
        borderDownColor: '#e08a7e',
        wickUpColor: '#5cc79a',
        wickDownColor: '#e08a7e'
      });

      // Subscribe to crosshair move
      chartRef.current.subscribeCrosshairMove((param) => {
        if (param.time && param.seriesData.size > 0 && candlestickSeriesRef.current) {
          const data = param.seriesData.get(candlestickSeriesRef.current) as CandlestickData;
          if (data && data.open != null) {
            setHoverData({
              time: param.time,
              open: data.open,
              high: data.high,
              low: data.low,
              close: data.close
            });
          }
        } else {
          setHoverData(null);
        }
      });
    }

    // Update candlestick data
    candlestickSeriesRef.current?.setData(candlestickData);

    // Set explicit visible range based on filtered data
    if (candlestickData.length > 0) {
      const firstTime = candlestickData[0].time;
      const lastTime = candlestickData[candlestickData.length - 1].time;
      chartRef.current.timeScale().setVisibleRange({ from: firstTime, to: lastTime });
    } else {
      chartRef.current.timeScale().fitContent();
    }

    // Set display data to latest bar when no hover
    if (!hoverData && candlestickData.length > 0) {
      const lastBar = candlestickData[candlestickData.length - 1];
      setDisplayData({
        time: lastBar.time,
        open: lastBar.open,
        high: lastBar.high,
        low: lastBar.low,
        close: lastBar.close
      });
    }

    // Handle resize
    const handleResize = () => {
      if (chartRef.current && chartContainerRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight
        });
      }
      if (ivChartRef.current && ivContainerRef.current) {
        ivChartRef.current.applyOptions({
          width: ivContainerRef.current.clientWidth,
          height: ivContainerRef.current.clientHeight
        });
      }
      if (rsiChartRef.current && rsiContainerRef.current) {
        rsiChartRef.current.applyOptions({
          width: rsiContainerRef.current.clientWidth,
          height: rsiContainerRef.current.clientHeight
        });
      }
      if (macdChartRef.current && macdContainerRef.current) {
        macdChartRef.current.applyOptions({
          width: macdContainerRef.current.clientWidth,
          height: macdContainerRef.current.clientHeight
        });
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, chartData, selectedPeriod, filterDataByPeriod, toSeconds, hoverData]);

  // Handle SMA 50 overlay (using pre-calculated values)
  useEffect(() => {
    if (!chartRef.current || chartData.length === 0) return;

    const filteredData = filterDataByPeriod(chartData, selectedPeriod);

    if (showSMA50) {
      if (!sma50SeriesRef.current) {
        sma50SeriesRef.current = chartRef.current.addSeries(LineSeries, {
          color: '#F59E0B', // Amber
          lineWidth: 1,
          priceLineVisible: false,
          lastValueVisible: false
        });
      }
      const smaData: LineData[] = filteredData
        .filter(bar => bar.sma_50 != null)
        .map(bar => ({
          time: toSeconds(bar.timestamp) as Time,
          value: bar.sma_50!
        }));
      sma50SeriesRef.current.setData(smaData);
    } else if (sma50SeriesRef.current) {
      chartRef.current.removeSeries(sma50SeriesRef.current);
      sma50SeriesRef.current = null;
    }
  }, [showSMA50, chartData, selectedPeriod, filterDataByPeriod, toSeconds]);

  // Handle SMA 200 overlay (using pre-calculated values)
  useEffect(() => {
    if (!chartRef.current || chartData.length === 0) return;

    const filteredData = filterDataByPeriod(chartData, selectedPeriod);

    if (showSMA200) {
      if (!sma200SeriesRef.current) {
        sma200SeriesRef.current = chartRef.current.addSeries(LineSeries, {
          color: '#3B82F6', // Blue
          lineWidth: 1,
          priceLineVisible: false,
          lastValueVisible: false
        });
      }
      const smaData: LineData[] = filteredData
        .filter(bar => bar.sma_200 != null)
        .map(bar => ({
          time: toSeconds(bar.timestamp) as Time,
          value: bar.sma_200!
        }));
      sma200SeriesRef.current.setData(smaData);
    } else if (sma200SeriesRef.current) {
      chartRef.current.removeSeries(sma200SeriesRef.current);
      sma200SeriesRef.current = null;
    }
  }, [showSMA200, chartData, selectedPeriod, filterDataByPeriod, toSeconds]);

  // Handle IV pane - recreate chart when period changes, align with price chart
  useEffect(() => {
    // Cleanup previous chart when period changes or when hiding
    if (ivChartRef.current) {
      ivChartRef.current.remove();
      ivChartRef.current = null;
      ivSeriesRef.current = null;
    }

    if (ivData.length === 0 || !showIV || !ivContainerRef.current || chartData.length === 0) return;

    const filteredIVData = filterIVDataByPeriod(ivData, selectedPeriod);
    const filteredPriceData = filterDataByPeriod(chartData, selectedPeriod);
    console.log(`📊 IV chart period ${selectedPeriod}: ${filteredIVData.length} of ${ivData.length} points`);

    // Create new chart
    ivChartRef.current = createChart(ivContainerRef.current, {
      localization: { locale: 'en-US' },
      layout: {
        background: { color: '#111215' },
        textColor: '#8b9097',
        attributionLogo: false
      },
      grid: {
        vertLines: { color: 'rgba(255, 255, 255, 0.045)' },
        horzLines: { color: 'rgba(255, 255, 255, 0.045)' }
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { width: 1, color: '#6f747c', style: 1 },
        horzLine: { width: 1, color: '#6f747c', style: 1 }
      },
      rightPriceScale: {
        borderColor: 'rgba(255, 255, 255, 0.08)',
        scaleMargins: { top: 0.1, bottom: 0.1 }
      },
      timeScale: {
        borderColor: 'rgba(255, 255, 255, 0.08)',
        visible: false
      },
      handleScroll: false,
      handleScale: false
    });

    ivSeriesRef.current = ivChartRef.current.addSeries(LineSeries, {
      color: '#F97316', // Orange
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true
    });

    // Set IV data
    const ivLineData: LineData[] = filteredIVData
      .filter(point => point.avg_iv != null)
      .map(point => ({
        time: toSeconds(point.timestamp) as Time,
        value: point.avg_iv!
      }));
    ivSeriesRef.current.setData(ivLineData);

    // Align time scale with price chart data range
    if (filteredPriceData.length > 0) {
      const firstTime = toSeconds(filteredPriceData[0].timestamp) as Time;
      const lastTime = toSeconds(filteredPriceData[filteredPriceData.length - 1].timestamp) as Time;
      ivChartRef.current.timeScale().setVisibleRange({ from: firstTime, to: lastTime });
    } else {
      ivChartRef.current.timeScale().fitContent();
    }

  }, [showIV, ivData, chartData, selectedPeriod, filterIVDataByPeriod, filterDataByPeriod, toSeconds]);

  // Handle RSI pane - recreate chart when period changes
  useEffect(() => {
    // Cleanup previous chart when period changes or when hiding
    if (rsiChartRef.current) {
      rsiChartRef.current.remove();
      rsiChartRef.current = null;
      rsiSeriesRef.current = null;
    }

    if (chartData.length === 0 || !showRSI || !rsiContainerRef.current) return;

    const filteredData = filterDataByPeriod(chartData, selectedPeriod);

    // Create new chart
    rsiChartRef.current = createChart(rsiContainerRef.current, {
      localization: { locale: 'en-US' },
      layout: {
        background: { color: '#111215' },
        textColor: '#8b9097',
        attributionLogo: false
      },
      grid: {
        vertLines: { color: 'rgba(255, 255, 255, 0.045)' },
        horzLines: { color: 'rgba(255, 255, 255, 0.045)' }
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { width: 1, color: '#6f747c', style: 1 },
        horzLine: { width: 1, color: '#6f747c', style: 1 }
      },
      rightPriceScale: {
        borderColor: 'rgba(255, 255, 255, 0.08)',
        scaleMargins: { top: 0.1, bottom: 0.1 }
      },
      timeScale: {
        borderColor: 'rgba(255, 255, 255, 0.08)',
        visible: false
      },
      handleScroll: false,
      handleScale: false
    });

    rsiSeriesRef.current = rsiChartRef.current.addSeries(LineSeries, {
      color: '#8B5CF6', // Purple
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true
    });

    // RSI reference lines (30 and 70)
    const rsiTimes = filteredData
      .filter(bar => bar.rsi_14 != null)
      .map(bar => toSeconds(bar.timestamp) as Time);

    if (rsiTimes.length > 0) {
      const line70 = rsiChartRef.current.addSeries(LineSeries, {
        color: '#374151',
        lineWidth: 1,
        lineStyle: 2,
        priceLineVisible: false,
        lastValueVisible: false
      });
      line70.setData(rsiTimes.map(time => ({ time, value: 70 })));

      const line30 = rsiChartRef.current.addSeries(LineSeries, {
        color: '#374151',
        lineWidth: 1,
        lineStyle: 2,
        priceLineVisible: false,
        lastValueVisible: false
      });
      line30.setData(rsiTimes.map(time => ({ time, value: 30 })));
    }

    // Set RSI data
    const rsiData: LineData[] = filteredData
      .filter(bar => bar.rsi_14 != null)
      .map(bar => ({
        time: toSeconds(bar.timestamp) as Time,
        value: bar.rsi_14!
      }));
    rsiSeriesRef.current.setData(rsiData);

    // Align time scale with price chart data range
    if (filteredData.length > 0) {
      const firstTime = toSeconds(filteredData[0].timestamp) as Time;
      const lastTime = toSeconds(filteredData[filteredData.length - 1].timestamp) as Time;
      rsiChartRef.current.timeScale().setVisibleRange({ from: firstTime, to: lastTime });
    } else {
      rsiChartRef.current.timeScale().fitContent();
    }

  }, [showRSI, chartData, selectedPeriod, filterDataByPeriod, toSeconds]);

  // Handle MACD pane - recreate chart when period changes
  useEffect(() => {
    // Cleanup previous chart when period changes or when hiding
    if (macdChartRef.current) {
      macdChartRef.current.remove();
      macdChartRef.current = null;
      macdLineSeriesRef.current = null;
      macdSignalSeriesRef.current = null;
      macdHistSeriesRef.current = null;
    }

    if (chartData.length === 0 || !showMACD || !macdContainerRef.current) return;

    const filteredData = filterDataByPeriod(chartData, selectedPeriod);

    // Skip if no MACD data available
    const hasMacdData = filteredData.some(bar => bar.macd_line != null);
    if (!hasMacdData) return;

    // Create new chart
    macdChartRef.current = createChart(macdContainerRef.current, {
      localization: { locale: 'en-US' },
      layout: {
        background: { color: '#111215' },
        textColor: '#8b9097',
        attributionLogo: false
      },
      grid: {
        vertLines: { color: 'rgba(255, 255, 255, 0.045)' },
        horzLines: { color: 'rgba(255, 255, 255, 0.045)' }
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { width: 1, color: '#6f747c', style: 1 },
        horzLine: { width: 1, color: '#6f747c', style: 1 }
      },
      rightPriceScale: {
        borderColor: 'rgba(255, 255, 255, 0.08)',
        scaleMargins: { top: 0.1, bottom: 0.1 }
      },
      timeScale: {
        borderColor: 'rgba(255, 255, 255, 0.08)',
        visible: false
      },
      handleScroll: false,
      handleScale: false
    });

    // MACD histogram — green when positive, red when negative
    macdHistSeriesRef.current = macdChartRef.current.addSeries(HistogramSeries, {
      priceLineVisible: false,
      lastValueVisible: false
    });

    const histData = filteredData
      .filter(bar => bar.macd_histogram != null)
      .map(bar => ({
        time: toSeconds(bar.timestamp) as Time,
        value: bar.macd_histogram!,
        color: bar.macd_histogram! >= 0 ? '#5cc79a' : '#e08a7e'
      }));
    macdHistSeriesRef.current.setData(histData);

    // MACD line — blue
    macdLineSeriesRef.current = macdChartRef.current.addSeries(LineSeries, {
      color: '#3B82F6',
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false
    });

    const macdLineData: LineData[] = filteredData
      .filter(bar => bar.macd_line != null)
      .map(bar => ({
        time: toSeconds(bar.timestamp) as Time,
        value: bar.macd_line!
      }));
    macdLineSeriesRef.current.setData(macdLineData);

    // Signal line — amber
    macdSignalSeriesRef.current = macdChartRef.current.addSeries(LineSeries, {
      color: '#F59E0B',
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false
    });

    const signalData: LineData[] = filteredData
      .filter(bar => bar.signal_line != null)
      .map(bar => ({
        time: toSeconds(bar.timestamp) as Time,
        value: bar.signal_line!
      }));
    macdSignalSeriesRef.current.setData(signalData);

    // Zero reference line — dashed gray
    const macdTimes = filteredData
      .filter(bar => bar.macd_line != null)
      .map(bar => toSeconds(bar.timestamp) as Time);

    if (macdTimes.length > 0) {
      const zeroLine = macdChartRef.current.addSeries(LineSeries, {
        color: '#374151',
        lineWidth: 1,
        lineStyle: 2,
        priceLineVisible: false,
        lastValueVisible: false
      });
      zeroLine.setData(macdTimes.map(time => ({ time, value: 0 })));
    }

    // Align time scale with price chart data range
    if (filteredData.length > 0) {
      const firstTime = toSeconds(filteredData[0].timestamp) as Time;
      const lastTime = toSeconds(filteredData[filteredData.length - 1].timestamp) as Time;
      macdChartRef.current.timeScale().setVisibleRange({ from: firstTime, to: lastTime });
    } else {
      macdChartRef.current.timeScale().fitContent();
    }

  }, [showMACD, chartData, selectedPeriod, filterDataByPeriod, toSeconds]);

  // Cleanup on close
  useEffect(() => {
    if (!isOpen) {
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        candlestickSeriesRef.current = null;
        sma50SeriesRef.current = null;
        sma200SeriesRef.current = null;
      }
      if (ivChartRef.current) {
        ivChartRef.current.remove();
        ivChartRef.current = null;
        ivSeriesRef.current = null;
      }
      if (rsiChartRef.current) {
        rsiChartRef.current.remove();
        rsiChartRef.current = null;
        rsiSeriesRef.current = null;
      }
      if (macdChartRef.current) {
        macdChartRef.current.remove();
        macdChartRef.current = null;
        macdLineSeriesRef.current = null;
        macdSignalSeriesRef.current = null;
        macdHistSeriesRef.current = null;
      }
      // Reset state
      setChartData([]);
      setIVData([]);
      setHoverData(null);
      setDisplayData(null);
    }
  }, [isOpen]);

  // Calculate price change for display
  const getDisplayData = () => {
    const data = hoverData || displayData;
    if (!data || data.open == null || data.close == null) return null;

    const change = data.close - data.open;
    const changePercent = data.open !== 0 ? (change / data.open) * 100 : 0;

    return {
      ...data,
      change,
      changePercent,
      isPositive: change >= 0
    };
  };

  const display = getDisplayData();

  if (!isOpen) return null;

  return createPortal(
    <div className="fullscreen-chart-modal-overlay" onClick={onClose}>
      <div className="fullscreen-chart-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Combined Header: Symbol + Name | Period | Indicators | Close */}
        <div className="fullscreen-chart-modal-header">
          <div className="fullscreen-chart-modal-title">
            <span className="fullscreen-chart-modal-symbol">{symbol}</span>
            {companyName && (
              <span className="fullscreen-chart-modal-company">{companyName}</span>
            )}
          </div>
          <div className="fullscreen-chart-modal-controls">
            <div className="toolbar-period-buttons">
              {(['1M', '3M', '6M'] as Period[]).map((period) => (
                <button
                  key={period}
                  className={`toolbar-period-btn ${selectedPeriod === period ? 'active' : ''}`}
                  onClick={() => setSelectedPeriod(period)}
                >
                  {period}
                </button>
              ))}
            </div>
            <div className="toolbar-indicator-buttons">
              <button
                className={`toolbar-indicator-btn ${showSMA50 ? 'active' : ''}`}
                onClick={() => setShowSMA50(!showSMA50)}
              >
                <span className="indicator-color sma50"></span>
                SMA 50
              </button>
              <button
                className={`toolbar-indicator-btn ${showSMA200 ? 'active' : ''}`}
                onClick={() => setShowSMA200(!showSMA200)}
              >
                <span className="indicator-color sma200"></span>
                SMA 200
              </button>
              <span className="toolbar-separator" />
              {ivData.length > 0 && (
                <button
                  className={`toolbar-indicator-btn ${showIV ? 'active' : ''}`}
                  onClick={() => setShowIV(!showIV)}
                >
                  <span className="indicator-color iv"></span>
                  IV
                </button>
              )}
              <button
                className={`toolbar-indicator-btn ${showRSI ? 'active' : ''}`}
                onClick={() => setShowRSI(!showRSI)}
              >
                <span className="indicator-color rsi"></span>
                RSI
              </button>
              <button
                className={`toolbar-indicator-btn ${showMACD ? 'active' : ''}`}
                onClick={() => setShowMACD(!showMACD)}
              >
                <span className="indicator-color macd"></span>
                MACD
              </button>
            </div>
          </div>
          <button className="fullscreen-chart-modal-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Chart Container with OHLC Overlay */}
        <div ref={wrapperRef} className="fullscreen-chart-modal-chart-wrapper">
          {/* OHLC Legend Overlay */}
          <div className="fullscreen-chart-modal-legend-overlay">
            {display ? (
              <>
                <span className="legend-item">
                  <span className="legend-label">O</span>
                  <span className="legend-value">{display.open.toFixed(2)}</span>
                </span>
                <span className="legend-item">
                  <span className="legend-label">H</span>
                  <span className="legend-value">{display.high.toFixed(2)}</span>
                </span>
                <span className="legend-item">
                  <span className="legend-label">L</span>
                  <span className="legend-value">{display.low.toFixed(2)}</span>
                </span>
                <span className="legend-item">
                  <span className="legend-label">C</span>
                  <span className="legend-value">{display.close.toFixed(2)}</span>
                </span>
                <span className={`legend-change ${display.isPositive ? 'positive' : 'negative'}`}>
                  {display.isPositive ? '+' : ''}{display.change.toFixed(2)} ({display.isPositive ? '+' : ''}{display.changePercent.toFixed(2)}%)
                </span>
              </>
            ) : isLoading ? (
              <span className="legend-placeholder">Loading...</span>
            ) : error ? (
              <span className="legend-placeholder legend-error">{error}</span>
            ) : null}
          </div>

          {isLoading ? (
            <div className="fullscreen-chart-loading">
              <div className="loading-spinner"></div>
              <span>Loading chart data...</span>
            </div>
          ) : error ? (
            <div className="fullscreen-chart-error">
              <span>{error}</span>
            </div>
          ) : (
            <>
              <div
                ref={chartContainerRef}
                className="fullscreen-chart-modal-chart"
                style={{ flex: 1, minHeight: 0 }}
              />
              {/* IV Pane */}
              {showIV && ivData.length > 0 && (
                <>
                  <div
                    className="pane-divider"
                    onMouseDown={(e) => handleDragStart('iv', e.clientY)}
                    onTouchStart={(e) => handleDragStart('iv', e.touches[0].clientY)}
                  />
                  <div
                    className="fullscreen-chart-modal-iv"
                    style={{ flex: `0 0 ${paneHeights.iv}px` }}
                  >
                    <span className="pane-inline-label iv-label">Option IV (%)</span>
                    <div ref={ivContainerRef} className="fullscreen-chart-modal-iv-chart" />
                  </div>
                </>
              )}
              {/* RSI Pane */}
              {showRSI && (
                <>
                  <div
                    className="pane-divider"
                    onMouseDown={(e) => handleDragStart('rsi', e.clientY)}
                    onTouchStart={(e) => handleDragStart('rsi', e.touches[0].clientY)}
                  />
                  <div
                    className="fullscreen-chart-modal-rsi"
                    style={{ flex: `0 0 ${paneHeights.rsi}px` }}
                  >
                    <span className="pane-inline-label rsi-label">RSI (14)</span>
                    <div ref={rsiContainerRef} className="fullscreen-chart-modal-rsi-chart" />
                  </div>
                </>
              )}
              {/* MACD Pane */}
              {showMACD && (
                <>
                  <div
                    className="pane-divider"
                    onMouseDown={(e) => handleDragStart('macd', e.clientY)}
                    onTouchStart={(e) => handleDragStart('macd', e.touches[0].clientY)}
                  />
                  <div
                    className="fullscreen-chart-modal-macd"
                    style={{ flex: `0 0 ${paneHeights.macd}px` }}
                  >
                    <span className="pane-inline-label macd-label">MACD (12,26,9)</span>
                    <div ref={macdContainerRef} className="fullscreen-chart-modal-macd-chart" />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FullscreenChartModal;
